<template>
    <rkm-form
        v-if="model"
        ref="RkmForm"
        show-legend
    >
        <rkm-row>
            <rkm-col cols="6">
                <rkm-form-input
                    :validations="validations.nome"
                    label="Nome"
                    v-model="model.nome"
                />
            </rkm-col>

            <rkm-col cols="6">
                <rkm-form-input-autocomplete
                    :repository="repositories.ocupacao"
                    :search-fields="fields.ocupacao"
                    :text-field="(item) => item.codigo ? `${item.codigo} - ${item.nome}` : null"
                    :validations="validations.ocupacao"
                    label="CBO"
                    v-model="model.ocupacao"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col>
                <rkm-form-select
                    :options="form.sexos"
                    @change="model.permiteAgendamentoGestante = false"
                    default-option-text="AMBOS"
                    label="Sexo"
                    show-default-option
                    text-field="descricao"
                    v-model="model.sexoId"
                    value-field="id"
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-input
                    :validations="validations.idadeMinima"
                    label="Idade mínima"
                    number
                    v-model="model.idadeMinima"
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-input
                    :validations="validations.idadeMaxima"
                    label="Idade máxima"
                    number
                    v-model="model.idadeMaxima"
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-input
                    :validations="validations.limiteConsulta"
                    label="Limite de consulta(s)"
                    number
                    v-model="model.limiteConsulta"
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-input
                    :validations="validations.limiteConsultaMes"
                    label="Limite em mes(es)"
                    number
                    v-model="model.limiteConsultaMes"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col>
                <rkm-form-select
                    :disabled="!model.isRegulado"
                    :label="false"
                    :options="form.regulacaoTipos"
                    :validations="validations.regulacaoTipo"
                    name="Tipo de regulação"
                    show-default-option
                    text-field="nome"
                    v-model="model.regulacaoTipoId"
                    value-field="id"
                >
                    <rkm-input-group-prepend slot="prepend">
                        <rkm-input-group-text>
                            <rkm-form-checkbox
                                @change="handleReguladoChange"
                                v-model="model.isRegulado"
                            >
                                Regulado
                            </rkm-form-checkbox>
                        </rkm-input-group-text>
                    </rkm-input-group-prepend>

                    <rkm-input-group-append slot="append">
                        <rkm-input-group-text>
                            <rkm-form-checkbox
                                :disabled="!model.isRegulado"
                                @change="handlePpiChange"
                                v-model="model.isPpi"
                            >
                                PPI
                            </rkm-form-checkbox>
                        </rkm-input-group-text>
                    </rkm-input-group-append>
                </rkm-form-select>
            </rkm-col>

            <rkm-col>
                <rkm-form-input
                    :disabled="!model.isPpi"
                    :validations="validations.valorPpi"
                    key="valorPpi"
                    label="Valor PPI"
                    prepend="R$"
                    ref="valorPpi"
                    v-model="model.valorPpi"
                    v-money="money"
                />
            </rkm-col>

            <rkm-col cols="6">
                <rkm-form-input-autocomplete
                    :disabled="!model.ocupacao"
                    :repository="repositories.procedimento"
                    :request-query-string-params="{
                        cbo: model.ocupacao ? model.ocupacao.codigo : null,
                        instrumento_registro: [
                            enums.registro.BPA_CONSOLIDADO,
                            enums.registro.RAAS_ATENCAO_PSICOSSOCIAL,
                        ],
                        search_by_apelido: searchByApelido,
                    }"
                    :search-fields="fields.procedimento"
                    :text-field="(item) => item.codigo ? `${formatProcedimento(item.codigo)} - ${item.nome}` : null"
                    label="Procedimento de grupo"
                    v-model="model.procedimentoGrupo"
                >
                    <rkm-input-group-append slot="append">
                        <rkm-input-group-text>
                            <rkm-form-radio-group v-model="searchByApelido">
                                <rkm-form-radio :value="false">Código / Nome</rkm-form-radio>
                                <rkm-form-radio :value="true">Apelido</rkm-form-radio>
                            </rkm-form-radio-group>
                        </rkm-input-group-text>
                    </rkm-input-group-append>
                </rkm-form-input-autocomplete>
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="4">
                <rkm-form-input
                    :validations="validations.limiteOcorrenciasPaciente"
                    label="Limite de ocorrências do paciente"
                    number
                    tooltipTop="Quantidade máxima de ocorrências do paciente (faltas e cancelamentos pelo paciente) permitidas dentro do período de monitoramento. Ao atingir o limite, as vagas ficam disponíveis somente a partir do prazo de liberação."
                    v-model="model.limiteOcorrenciasPaciente"
                />
            </rkm-col>

            <rkm-col cols="4">
                <rkm-form-input
                    :validations="validations.periodoMonitoramento"
                    label="Período de monitoramento (dias)"
                    number
                    tooltipTop="Período (em dias) usado para contar ocorrências recentes do paciente. Exemplo: 90 dias considera apenas faltas e cancelamentos pelo paciente nos últimos 90 dias."
                    v-model="model.periodoMonitoramento"
                />
            </rkm-col>

            <rkm-col cols="4">
                <rkm-form-input
                    :validations="validations.tempoLiberacao"
                    label="Tempo de liberação (dias)"
                    number
                    tooltipTop="Quantidade de dias a partir da última ocorrência do paciente para liberar novas vagas. Antes desse prazo, o cidadão não verá vagas anteriores, mesmo que existam."
                    v-model="model.tempoLiberacao"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="12">
                <rkm-form-textarea
                    :validations="validations.mensagemProtocoloAgendamento"
                    label="Mensagem de protocolo do agendamento"
                    v-model="model.mensagemProtocoloAgendamento"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="4">
                <rkm-form-checkbox v-model="model.permiteFechamentoSemProcedimento">
                    Permite fechamento sem procedimento
                </rkm-form-checkbox>

                <rkm-form-checkbox
                    :disabled="model.sexoId === enums.sexo.MASCULINO"
                    v-model="model.permiteAgendamentoGestante"
                >
                    Permite agendamento para gestante
                </rkm-form-checkbox>

                <rkm-form-checkbox v-model="model.permiteAgendamentoMesmoDia">
                    Permite agendamento para o mesmo dia
                </rkm-form-checkbox>
            </rkm-col>

            <rkm-col cols="4">
                <rkm-form-checkbox v-model="model.permiteAgendamentoNaoMunicipe">
                    Permite agendamento para não munícipe
                </rkm-form-checkbox>

                <rkm-form-checkbox v-model="model.tratamento">
                    Permite agendar tratamento
                </rkm-form-checkbox>

                <rkm-form-checkbox v-model="model.exigirPesoAlturaCircunferencia">
                    Exige preenchimento de peso, altura e circunferência abdominal para solicitação de encaminhamento
                </rkm-form-checkbox>
            </rkm-col>

            <rkm-col cols="4">
                <rkm-form-checkbox v-model="model.encaminhamento">
                    Permite ser encaminhada para atendimento
                </rkm-form-checkbox>

                <rkm-form-checkbox
                    :disabled="!model.tratamento"
                    v-model="model.precisaInformarConclusaoTratamento"
                >
                    Precisa informar conclusão do tratamento
                </rkm-form-checkbox>

                <rkm-form-checkbox v-model="model.permiteIntegracaoConectasusCidadao">
                    Integração ConectaSUS Cidadão
                    <rkm-icon
                        icon="info"
                        color="secondary"
                        class="infor"
                        small
                        v-rkm-popover.hover.left="{
                            html: true,
                            content: 'Quando ativado, esta especialidade poderá ser exibida e consumida no ConectaSUS Cidadão.'
                        }"
                    />
                </rkm-form-checkbox>
            </rkm-col>
        </rkm-row>

        <rkm-row class="mt-2">
            <rkm-col cols="12">
                <rkm-form-input-autocomplete
                    :disabled="!model.ocupacao"
                    :repository="repositories.procedimento"
                    :request-query-string-params="{
                        only_without_cid: true,
                        cbo: model.ocupacao ? model.ocupacao.codigo : null,
                        search_by_apelido: searchByApelidoAtencaoBasica,
                    }"
                    :search-fields="fields.procedimento"
                    :text-field="(item) => item.codigo ? `${formatProcedimento(item.codigo)} - ${item.nome}` : null"
                    label="Procedimento padrão"
                    v-model="model.atencaoBasica.procedimentoPadrao"
                >
                    <rkm-input-group-append slot="append">
                        <rkm-input-group-text>
                            <rkm-form-radio-group v-model="searchByApelidoAtencaoBasica">
                                <rkm-form-radio :value="false">Código / Nome</rkm-form-radio>
                                <rkm-form-radio :value="true">Apelido</rkm-form-radio>
                            </rkm-form-radio-group>
                        </rkm-input-group-text>
                    </rkm-input-group-append>
                </rkm-form-input-autocomplete>
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="12">
                <rkm-area
                    :class="{'pb-0': !visibleAtencaoBasica}"
                    title="Configurações - Atenção básica"
                    type="fieldset"
                >
                    <template slot="trailing">
                        <rkm-button
                            @click="visibleAtencaoBasica = !visibleAtencaoBasica"
                            color="primary"
                            small
                        >
                            <rkm-icon :icon="visibleAtencaoBasica ? 'angle-double-up' : 'angle-double-down'" />
                        </rkm-button>
                    </template>

                    <rkm-collapse :visible="visibleAtencaoBasica">
                        <rkm-row>
                            <rkm-col cols="12">
                                <rkm-area
                                    title="Informações complementares"
                                    type="fieldset"
                                >
                                    <rkm-row>
                                        <rkm-col cols="12">
                                            <rkm-form-select
                                                :disabled="!isProcedimentoPadraoRaas"
                                                :options="form.atencaoBasica.motivosSaidaPermacencia"
                                                :validations="validations.atencaoBasica.motivoSaidaPermanencia"
                                                label="Motivo de saída/permanência"
                                                show-default-option
                                                text-field="nome"
                                                v-model="model.atencaoBasica.motivoSaidaPermanenciaId"
                                                value-field="id"
                                            />
                                        </rkm-col>
                                    </rkm-row>

                                    <rkm-row>
                                        <rkm-col cols="8">
                                            <rkm-form-select
                                                :disabled="!procedimentoPadraoPrecisaClassificacao"
                                                :options="form.atencaoBasica.classificacoesServico"
                                                :validations="validations.atencaoBasica.classificacao"
                                                label="Classificação de serviço"
                                                show-default-option
                                                text-field="nome"
                                                v-model="model.atencaoBasica.classificacaoCodigo"
                                                value-field="codigo"
                                            />
                                        </rkm-col>

                                        <rkm-col cols="4">
                                            <rkm-form-input
                                                :disabled="!procedimentoPadraoPrecisaAutorizacao"
                                                :validations="validations.atencaoBasica.numeroAutorizacao"
                                                label="Autorização"
                                                v-model="model.atencaoBasica.numeroAutorizacao"
                                            />
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-area>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col cols="12">
                                <rkm-area
                                    title="CIAP / CID"
                                    type="fieldset"
                                >
                                    <rkm-row>
                                        <rkm-col cols="3">
                                            <rkm-form-select
                                                :options="form.atencaoBasica.problemaCondicao.situacoes"
                                                label="Situação"
                                                show-default-option
                                                text-field="nome"
                                                v-model="model.atencaoBasica.problemaCondicao.situacaoId"
                                                value-field="id"
                                            />
                                        </rkm-col>

                                        <rkm-col cols="4">
                                            <rkm-form-input-autocomplete
                                                :repository="repositories.geral.ciap"
                                                :request-query-string-params="{
                                                    cid_id: model.atencaoBasica.problemaCondicao.cid
                                                        ? model.atencaoBasica.problemaCondicao.cid.id
                                                        : null,
                                                    show_all: true,
                                                    not_in: [
                                                        enums.ciap.MEDICINA_FISICA_REABILITACAO,
                                                        enums.ciap.TUBERCULOSE,
                                                        enums.ciap.DENGUE_E_OUTRAS_DOENCAS_VIRAIS_NE,
                                                        enums.ciap.HANSENIASE_E_OUTRAS_DOENCAS_INFECCIOSAS_NE,
                                                        enums.ciap.FATOR_DE_RISCO_PARA_DOENCA_CARDIOVASCULAR,
                                                        enums.ciap.HIPERTENSAO_SEM_COMPLICACOES,
                                                        enums.ciap.ABUSO_AGUDO_DE_ALCOOL,
                                                        enums.ciap.ABUSO_DO_TABACO,
                                                        enums.ciap.ABUSO_DE_DROGAS,
                                                        enums.ciap.DOENCA_PULMONAR_OBSTRUTIVA_CRONICA,
                                                        enums.ciap.ASMA,
                                                        enums.ciap.OBESIDADE,
                                                        enums.ciap.DIABETES_NAO_INSULINO_DEPENDENTE,
                                                        enums.ciap.DEFICIENCIA_VITAMINICA_NUTRICIONAL,
                                                        enums.ciap.SINAIS_SINTOMAS_POS_PARTO,
                                                    ],
                                                }"
                                                :search-fields="fields.ciap"
                                                :text-field="(item) => `${item.codigo} - ${item.descricao}`"
                                                label="CIAP padrão"
                                                v-model="model.atencaoBasica.problemaCondicao.ciap"
                                            />
                                        </rkm-col>

                                        <rkm-col cols="5">
                                            <rkm-form-input-autocomplete
                                                :repository="repositories.cid"
                                                :request-query-string-params="{
                                                    ciap_id: model.atencaoBasica.problemaCondicao.ciap
                                                        ? model.atencaoBasica.problemaCondicao.ciap.id
                                                        : null,
                                                }"
                                                :search-fields="fields.cid"
                                                :text-field="(item) => `${item.codigo} - ${item.nome}`"
                                                label="CID padrão"
                                                v-model="model.atencaoBasica.problemaCondicao.cid"
                                            />
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-area>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col cols="12">
                                <rkm-area
                                    title="Condutas"
                                    type="fieldset"
                                >
                                    <rkm-row>
                                        <rkm-col cols="12">
                                            <rkm-form-checkbox-group
                                                :options="form.atencaoBasica.condutas"
                                                text-field="descricao"
                                                v-model="model.atencaoBasica.condutas"
                                                value-field="id"
                                            />
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-area>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col cols="12">
                                <rkm-area
                                    title="Detalhes do atendimento odontológico"
                                    type="fieldset"
                                >
                                    <rkm-row>
                                        <rkm-col cols="6">
                                            <rkm-form-select
                                                :options="form.atencaoBasica.odontologico.consultaTipos"
                                                label="Tipo de consulta"
                                                show-default-option
                                                text-field="descricao"
                                                v-model="model.atencaoBasica.odontologico.consultaTipoId"
                                                value-field="id"
                                            />
                                        </rkm-col>

                                        <rkm-col cols="6">
                                            <rkm-form-select
                                                :options="form.atencaoBasica.odontologico.vigilancias"
                                                label="Vigilância em saúde bucal"
                                                show-default-option
                                                text-field="nome"
                                                v-model="model.atencaoBasica.odontologico.vigilanciaId"
                                                value-field="id"
                                            />
                                        </rkm-col>
                                    </rkm-row>

                                    <rkm-row>
                                        <rkm-col cols="6">
                                            <rkm-form-select
                                                :options="form.atencaoBasica.odontologico.condutas"
                                                label="Conduta/desfecho"
                                                show-default-option
                                                text-field="descricao"
                                                v-model="model.atencaoBasica.odontologico.condutaId"
                                                value-field="id"
                                            />
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-area>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col cols="12">
                                <rkm-area
                                    title="Condições avaliadas"
                                    type="fieldset"
                                >
                                    <rkm-row>
                                        <rkm-col cols="12">
                                            <rkm-form-checkbox-group
                                                :options="form.atencaoBasica.condicoesAvaliadas"
                                                text-field="descricao"
                                                v-model="model.atencaoBasica.condicoesAvaliadas"
                                                value-field="id"
                                            />
                                        </rkm-col>
                                    </rkm-row>
                                </rkm-area>
                            </rkm-col>
                        </rkm-row>
                    </rkm-collapse>
                </rkm-area>
            </rkm-col>
        </rkm-row>
    </rkm-form>
</template>

<script>
// Directives.
import {VMoney} from 'v-money'

// Enums.
import registro from '@/enums/sigtap/registro'
import procedimento from '@/enums/sigtap/procedimento'
import sexo from '@/enums/cadastro/cidadao/sexo'
import ciap from '@/enums/geral/ciap'

// Mixins.
import {error, formats, repository, stringCase} from '@/mixins'

// Vuex.
import {mapActions} from 'vuex'

export default {
    computed: {
        fields() {
            return {
                procedimento: this.searchByApelido
                    ? [{key: 'parametrizacao_procedimento.apelido', searchable: true}]
                    : [{key: 'codigo', searchable: true}, {key: 'nome', searchable: true}],
                ocupacao: [
                    {key: 'codigo', searchable: true},
                    {key: 'nome', searchable: true},
                ],
                ciap: [
                    {key: 'geral_ciap.codigo', searchable: true},
                    {key: 'geral_ciap.descricao', searchable: true},
                ],
                cid: [
                    {key: 'codigo', searchable: true},
                    {key: 'nome', searchable: true},
                ],
            }
        },
        validations() {
            return {
                nome: {
                    required: true,
                    min: 3,
                    max: 100,
                },
                ocupacao: {required: true},
                idadeMinima: {
                    max: 3,
                    min_value: 1,
                    max_value: this.model.idadeMaxima || 130,
                },
                idadeMaxima: {
                    max: 3,
                    min_value: this.model.idadeMinima || 1,
                    max_value: 130,
                },
                limiteConsulta: {
                    required: !!this.model.limiteConsultaMes,
                    max: 4,
                    min_value: 1,
                    max_value: 9999,
                },
                limiteConsultaMes: {
                    required: !!this.model.limiteConsulta,
                    max: 2,
                    min_value: 1,
                    max_value: 12,
                },
                limiteOcorrenciasPaciente: {
                    min_value: 1,
                    max: 4,
                },
                periodoMonitoramento: {
                    min_value: 1,
                    max: 4,
                },
                tempoLiberacao: {
                    min_value: 0,
                    max: 4,
                },
                mensagemProtocoloAgendamento: {max: 500},
                regulacaoTipo: {
                    required: this.model.isRegulado,
                },
                atencaoBasica: {
                    motivoSaidaPermanencia: {required: this.isProcedimentoPadraoRaas},
                    classificacao: {required: this.procedimentoPadraoPrecisaClassificacao},
                    numeroAutorizacao: {required: this.procedimentoPadraoPrecisaAutorizacao, max: 13},
                }
            }
        },
        isProcedimentoPadraoRaas() {

            if (!this.model.atencaoBasica.procedimentoPadrao) {

                return false;
            }

            return this.model.atencaoBasica.procedimentoPadrao.hasInstrumentoRegistroRaas
        },
        procedimentoPadraoPrecisaClassificacao() {

            if (!this.model.atencaoBasica.procedimentoPadrao) {

                return false;
            }

            return this.model.atencaoBasica.procedimentoPadrao.codigo === this.enums.procedimento.ACOMPANHAMENTO_DE_PACIENTE_EM_SAUDE_MENTAL
        },
        procedimentoPadraoPrecisaAutorizacao() {

            if (!this.model.atencaoBasica.procedimentoPadrao) {

                return false;
            }

            return [
                this.enums.procedimento.ACOMPANHAMENTO_DE_PACIENTE_EM_SAUDE_MENTAL,
                this.enums.procedimento.ACOMPANHAMENTO_DE_PESSOAS_COM_NECESSIDADES_DECORRENTES_DO_USO_DE_ALCOOL_CRACK_E_OUTRAS_DROGAS,
                this.enums.procedimento.ACOMPANHAMENTO_DE_PESSOAS_ADULTAS_COM_SOFRIMENTO_OU_TRANSTORNOS_MENTAIS_DECORRENTES_DO_USO_DE_CRACK_ALCOOL_E_OUTRAS_DROGAS,
                this.enums.procedimento.ACOMPANHAMENTO_DA_POPULACAO_INFANTO_JUVENIL_COM_SOFRIMENTO_OU_TRANSTORNOS_MENTAIS_DECORRENTES_DO_USO_DE_CRACK_ALCOOL_E_OUTRAS_DROGAS,
            ].includes(this.model.atencaoBasica.procedimentoPadrao.codigo)
        },
    },
    created() {
        this.repositories = {
            especialidade: this.getConectaSusRepository('cadastro.especialidade'),
            ocupacao: this.getConectaSusRepository('sigtap.ocupacao'),
            procedimento: this.getConectaSusRepository('sigtap.procedimento'),
            cid: this.getConectaSusRepository('sigtap.cid'),
            geral: this.getConectaSusRepository('geral'),
        }
    },
    data: () => ({
        enums: {
            ciap,
            procedimento,
            registro,
            sexo,
        },
        form: null,
        model: null,
        money: {
            decimal: ',',
            masked: false,
            precision: 2,
            suffix: '',
            thousands: '.',
        },
        searchByApelido: false,
        searchByApelidoAtencaoBasica: false,
        visibleAtencaoBasica: false,
    }),
    directives: {money: VMoney},
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        create() {
            this.repositories.especialidade.create()
                .then(response => {
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)
                    this.addWathOcupacao()
                    this.addWathProcedimentoPadrao()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        store(request) {
            this.repositories.especialidade.store(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.showSaveNotification()
                    this.$emit('saved', response)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        edit(id) {
            this.repositories.especialidade.edit(id)
                .then(response => {
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)
                    this.addWathOcupacao()
                    this.addWathProcedimentoPadrao()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        update(id, request) {
            this.repositories.especialidade
                .update(id, this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.showSaveNotification()
                    this.$emit('saved', response)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        load() {
            this.setLoading()

            if (this.id) {
                this.edit(this.id)
            } else {
                this.create()
            }
        },
        save() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    this.setLoading()

                    const request = {
                        encaminhamento: this.model.encaminhamento,
                        idadeMaxima: this.model.idadeMaxima,
                        idadeMinima: this.model.idadeMinima,
                        isRegulado: this.model.isRegulado,
                        limiteConsulta: this.model.limiteConsulta,
                        limiteConsultaMes: this.model.limiteConsultaMes,
                        mensagemProtocoloAgendamento: this.model.mensagemProtocoloAgendamento,
                        nome: this.model.nome,
                        ocupacaoId: this.model.ocupacao?.id,
                        permiteAgendamentoGestante: this.model.permiteAgendamentoGestante,
                        permiteAgendamentoMesmoDia: this.model.permiteAgendamentoMesmoDia,
                        permiteAgendamentoNaoMunicipe: this.model.permiteAgendamentoNaoMunicipe,
                        permiteFechamentoSemProcedimento: this.model.permiteFechamentoSemProcedimento,
                        precisaInformarConclusaoTratamento: this.model.precisaInformarConclusaoTratamento,
                        permiteIntegracaoConectasusCidadao: this.model.permiteIntegracaoConectasusCidadao,
                        exigirPesoAlturaCircunferencia: this.model.exigirPesoAlturaCircunferencia,
                        procedimentoGrupoCodigo: this.model.procedimentoGrupo?.codigo ?? null,
                        regulacaoTipoId: this.model.regulacaoTipoId,
                        sexoId: this.model.sexoId,
                        tratamento: this.model.tratamento,
                        atencaoBasica: {
                            procedimentoPadraoCodigo: this.model.atencaoBasica.procedimentoPadrao?.codigo ?? null,
                            motivoSaidaPermanenciaId: this.model.atencaoBasica.motivoSaidaPermanenciaId,
                            classificacaoCodigo: this.model.atencaoBasica.classificacaoCodigo,
                            numeroAutorizacao: this.model.atencaoBasica.numeroAutorizacao,
                            problemaCondicao: {
                                situacaoId: this.model.atencaoBasica.problemaCondicao.situacaoId,
                                ciapId: this.model.atencaoBasica.problemaCondicao.ciap?.id ?? null,
                                cidId: this.model.atencaoBasica.problemaCondicao.cid?.id ?? null,
                            },
                            condutas: this.model.atencaoBasica.condutas,
                            odontologico: {
                                consultaTipoId: this.model.atencaoBasica.odontologico.consultaTipoId,
                                vigilanciaId: this.model.atencaoBasica.odontologico.vigilanciaId,
                                condutaId: this.model.atencaoBasica.odontologico.condutaId,
                            },
                            condicoesAvaliadas: this.model.atencaoBasica.condicoesAvaliadas,
                        },
                        isPpi: this.model.isPpi,
                        valorPpi: this.model.valorPpi,
                        limiteOcorrenciasPaciente: this.model.limiteOcorrenciasPaciente,
                        periodoMonitoramento: this.model.periodoMonitoramento,
                        tempoLiberacao: this.model.tempoLiberacao,
                    }

                    if (this.id) {
                        this.update(this.id, request)
                    } else {
                        this.store(request)
                    }
                })
        },
        addWathOcupacao() {
            this.$watch('model.ocupacao', function () {
                this.model.procedimentoGrupo = null
                this.model.atencaoBasica.procedimentoPadrao = null
            })
        },
        addWathProcedimentoPadrao() {
            this.$watch('model.atencaoBasica.procedimentoPadrao', function () {
                this.model.atencaoBasica.motivoSaidaPermanenciaId = null
                this.model.atencaoBasica.classificacaoCodigo = null
                this.model.atencaoBasica.numeroAutorizacao = null
            })
        },
        handleReguladoChange() {
            this.model.regulacaoTipoId = null
            this.model.isPpi = false

            this.handlePpiChange()
        },
        handlePpiChange() {
            this.model.valorPpi = '0,00'
            this.$refs.valorPpi.$el.getElementsByTagName('input')[0].value = '0,00'
        },
    },
    mixins: [error, formats, repository, stringCase],
    mounted() {
        this.load()
    },
    props: {
        id: {type: [Number, String], default: null},
    },
    watch: {
        'model.tratamento'(value) {
            if(!value) {
                this.model.precisaInformarConclusaoTratamento = false
            }
        },
    }
}
</script>

<style scoped>
.infor {
    color: #acacac;
    height: 15px;
    width: 15px !important;
    border-radius: 31px;
    padding: 3px;
    border: 1px solid #c2c2c2;
    vertical-align: middle;
    margin-left: 4px;
}
</style>